/**
 * demo-role.ts — Demo Mode role persistence helper
 *
 * Single source of truth for the demo role stored in localStorage. Both
 * DemoAuthProvider (which mints the fake user's `appRole`) and DemoRoleSwitcher
 * (which lets the presenter change it) read/write through here so the storage
 * key and the valid-value validation live in exactly one place.
 */
import type { UserRole } from '@philofund/shared/types/user';

/** localStorage key holding the currently selected demo role. */
export const DEMO_ROLE_STORAGE_KEY = 'demo_role';

/** Default role when nothing valid is persisted yet. */
export const DEFAULT_DEMO_ROLE: UserRole = 'donor';

const VALID_DEMO_ROLES: readonly UserRole[] = ['donor', 'org_admin', 'platform_admin'];

/**
 * Stored value meaning "signed out": the demo mints no user, so the public
 * pages render exactly as an anonymous visitor sees them.
 */
export const DEMO_GUEST = 'guest';

/** A demo role selection — one of the three app roles, or the signed-out visitor. */
export type DemoRoleSelection = UserRole | typeof DEMO_GUEST;

/** True when the presenter has chosen the signed-out visitor view. */
export function isDemoGuest(): boolean {
  return localStorage.getItem(DEMO_ROLE_STORAGE_KEY) === DEMO_GUEST;
}

/**
 * Read the persisted demo role, falling back to the default when the stored
 * value is missing or not one of the three valid roles.
 */
export function readDemoRole(): UserRole {
  const stored = localStorage.getItem(DEMO_ROLE_STORAGE_KEY);
  if (stored !== null && (VALID_DEMO_ROLES as readonly string[]).includes(stored)) {
    return stored as UserRole;
  }
  return DEFAULT_DEMO_ROLE;
}
